/* ═══════════════════════════════════════════════════════════════
   useModuleCategoryBrowser — the category TREE, navigation and CRUD
   dialogs' state for `ModuleBrowser` (migration 056). Split out of
   ModuleBrowser.tsx mechanically (no behavior change) so that file stays
   readable; every piece here is exactly what it was before, just moved.

   Owns: the category tree's fetch + live sync, the breadcrumb/drill-down
   navigation (remembered per user per host), the New/Rename/Move/Delete
   dialogs' state and handlers (delete reparents, never deletes a module),
   and the per-location module counts/entries the host renders as cards.

   `ModuleBrowser` still owns columns, layout and the table itself — this
   hook never touches `module_library` directly, only `module_categories`
   through `CategoriesService`.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { hasPermission } from '../../shared/permissions';
import { Prefs } from '../services/prefsService';
import { useTableSync } from '../hooks/useTableSync';
import { CategoriesService } from '../services/categoriesService';
import {
  buildCategoryTree,
  categoryPath,
  categoryPathLabel,
  descendantCategoryIds,
  moduleCountByCategory,
  modulesInCategory,
  moduleEffectiveCategoryId,
  wouldCreateCycle,
  type CategoryTreeNode,
  type ModuleCategory,
} from '../../shared/module-categories';
import type { SpecModule } from '../../shared/spec-modules';
import { applyTagFilter, tagsInScope, toggleTagSelection, type TagCount } from '../lib/moduleTagFilter';

// ─── Location: where the browser is standing ───

export type BrowserLocation =
  | { kind: 'root' }
  | { kind: 'category'; id: string }
  | { kind: 'uncategorized' };
export const ROOT: BrowserLocation = { kind: 'root' };

export function findNode(id: string, nodes: CategoryTreeNode[]): CategoryTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(id, n.children);
    if (found) return found;
  }
  return null;
}

const reason = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export type ModuleCategoryEntry =
  | { kind: 'category'; cat: ModuleCategory; count: number }
  | { kind: 'uncategorized'; count: number };

export interface UseModuleCategoryBrowserArgs {
  /** Scopes the "last open category" preference — unique per surface
   *  (`library`, `panel`). */
  hostId: string;
  /** Already loaded and filtered by the host (e.g. the archive toggle). */
  modules: SpecModule[];
  userName: string;
  userPermissions: string[];
  toast: (msg: string, isError?: boolean) => void;
  /** Fires whenever the category list (re)loads, so a host that needs the
   *  tree itself doesn't have to run a second fetch of the same table. */
  onCategoriesChange?: (categories: ModuleCategory[]) => void;
}

export interface ModuleCategoryBrowserState {
  canCreateCategory: boolean;
  canEditCategory: boolean;
  canDeleteCategory: boolean;

  categories: ModuleCategory[];
  categoriesLoaded: boolean;
  tree: CategoryTreeNode[];

  location: BrowserLocation;
  goTo: (loc: BrowserLocation) => void;
  currentNode: CategoryTreeNode | null;
  showAllCats: boolean;
  setShowAllCats: (show: boolean) => void;

  counts: Record<string, number>;
  uncategorizedCount: number;
  subNodes: CategoryTreeNode[];
  showUncategorizedEntry: boolean;
  entries: ModuleCategoryEntry[];

  tableModules: SpecModule[];
  crumbs: ModuleCategory[];
  currentLabel: string;

  // ─── Tags rail (click-to-filter, ANDs with the category scope) ───
  tagsInScope: TagCount[];
  selectedTags: string[];
  toggleTag: (tag: string) => void;
  clearTags: () => void;
  /** The category has modules, but the tag filter narrowed them to none —
   *  distinct from a genuinely empty category, so the table's empty state
   *  can point at the tag filter (and offer Clear) instead of "no modules
   *  here yet". */
  tagFilterEmptiedResults: boolean;

  // ─── New category ───
  newOpen: boolean;
  /** Set ⇒ the new category is added as a sub-category of this parent;
   *  null ⇒ added at the top level. Drives the dialog's own description. */
  newParentId: string | null;
  newName: string;
  setNewName: (name: string) => void;
  newBusy: boolean;
  newError: string | null;
  openNew: (parentId: string | null) => void;
  setNewOpen: (open: boolean) => void;
  doNew: () => Promise<void>;

  // ─── Rename ───
  renameTarget: ModuleCategory | null;
  renameName: string;
  setRenameName: (name: string) => void;
  renameBusy: boolean;
  renameError: string | null;
  openRename: (cat: ModuleCategory) => void;
  setRenameTarget: (cat: ModuleCategory | null) => void;
  doRename: () => Promise<void>;

  // ─── Move to… ───
  moveTarget: ModuleCategory | null;
  moveTo: string;
  setMoveTo: (id: string) => void;
  moveBusy: boolean;
  moveError: string | null;
  moveOptions: { value: string; label: string }[];
  openMove: (cat: ModuleCategory) => void;
  setMoveTarget: (cat: ModuleCategory | null) => void;
  doMove: () => Promise<void>;

  // ─── Delete (reparents, never deletes a module) ───
  deleteTarget: ModuleCategory | null;
  setDeleteTarget: (cat: ModuleCategory | null) => void;
  deleteBusy: boolean;
  deleteDirectModules: number;
  deleteDirectChildren: number;
  deleteParentLabel: string;
  doDelete: () => Promise<void>;
}

export function useModuleCategoryBrowser({
  hostId,
  modules,
  userName,
  userPermissions,
  toast,
  onCategoriesChange,
}: UseModuleCategoryBrowserArgs): ModuleCategoryBrowserState {
  const canCreateCategory = hasPermission(userPermissions, 'modules.create');
  const canEditCategory = hasPermission(userPermissions, 'modules.edit');
  const canDeleteCategory = hasPermission(userPermissions, 'modules.delete');

  // ─── The category tree ───
  const [categories, setCategories] = React.useState<ModuleCategory[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = React.useState(false);
  const loadCategories = React.useCallback(async () => {
    const rows = await CategoriesService.listAll();
    setCategories(rows);
    setCategoriesLoaded(true);
    onCategoriesChange?.(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    void loadCategories();
  }, [loadCategories]);
  useTableSync(['module_categories'], () => void loadCategories());

  const tree = React.useMemo(() => buildCategoryTree(categories), [categories]);

  // ─── Navigation, remembered per user per host ───
  const prefKey = `module-browser.lastCategory.${hostId}`;
  const [location, setLocation] = React.useState<BrowserLocation>(() => {
    const saved = Prefs.get(prefKey);
    if (saved && typeof saved === 'object' && typeof saved.kind === 'string') {
      return saved as BrowserLocation;
    }
    return ROOT;
  });
  const [showAllCats, setShowAllCats] = React.useState(false);

  const goTo = React.useCallback(
    (loc: BrowserLocation) => {
      setLocation(loc);
      setShowAllCats(false);
      Prefs.set(prefKey, loc);
    },
    [prefKey]
  );

  const currentNode = location.kind === 'category' ? findNode(location.id, tree) : null;

  // A category deleted out from under the current view drops back to root.
  React.useEffect(() => {
    if (categoriesLoaded && location.kind === 'category' && !currentNode) goTo(ROOT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesLoaded, location, currentNode]);

  const counts = React.useMemo(
    () => moduleCountByCategory(modules, categories),
    [modules, categories]
  );
  const uncategorizedCount = React.useMemo(
    () => modules.filter((m) => moduleEffectiveCategoryId(m, categories) === null).length,
    [modules, categories]
  );

  const subNodes: CategoryTreeNode[] =
    location.kind === 'root'
      ? tree
      : location.kind === 'category'
        ? currentNode?.children || []
        : [];
  const showUncategorizedEntry = location.kind === 'root' && uncategorizedCount > 0;

  const entries: ModuleCategoryEntry[] = [
    ...subNodes.map((n) => ({ kind: 'category' as const, cat: n, count: counts[n.id] || 0 })),
    ...(showUncategorizedEntry
      ? [{ kind: 'uncategorized' as const, count: uncategorizedCount }]
      : []),
  ];

  // Modules in the current CATEGORY scope only, before the tag filter —
  // the tag universe (`tagsInScope`) and chip counts are faceted against
  // this, never against the already tag-narrowed `tableModules` below.
  const categoryScopedModules = React.useMemo(() => {
    if (location.kind === 'uncategorized') return modulesInCategory(modules, null, categories);
    if (location.kind === 'category') {
      return modulesInCategory(modules, location.id, categories, { includeDescendants: true });
    }
    return modules;
  }, [location, modules, categories]);

  // ─── Tags rail: selection is per-session (never persisted), and never
  // reset by `goTo` — drilling into a category keeps the user's tags
  // selected even if that category has none, so the empty result still
  // reads as "these tags, here" rather than quietly clearing the filter.
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const toggleTag = React.useCallback(
    (tag: string) => setSelectedTags((prev) => toggleTagSelection(prev, tag)),
    []
  );
  const clearTags = React.useCallback(() => setSelectedTags([]), []);

  const tagsInScopeList = React.useMemo(
    () => tagsInScope(categoryScopedModules, selectedTags),
    [categoryScopedModules, selectedTags]
  );

  const tableModules = React.useMemo(
    () => applyTagFilter(categoryScopedModules, selectedTags),
    [categoryScopedModules, selectedTags]
  );

  const tagFilterEmptiedResults =
    selectedTags.length > 0 && categoryScopedModules.length > 0 && tableModules.length === 0;

  const crumbs = location.kind === 'category' ? categoryPath(location.id, categories) : [];
  const currentLabel =
    location.kind === 'root'
      ? 'All modules'
      : location.kind === 'uncategorized'
        ? 'Uncategorized'
        : currentNode?.name || '';

  // ─── New category ───
  const [newOpen, setNewOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newBusy, setNewBusy] = React.useState(false);
  const [newError, setNewError] = React.useState<string | null>(null);
  const [newParentId, setNewParentId] = React.useState<string | null>(null);

  const openNew = (parentId: string | null) => {
    setNewParentId(parentId);
    setNewName('');
    setNewError(null);
    setNewOpen(true);
  };
  const doNew = async () => {
    const name = newName.trim();
    if (!name) return;
    setNewBusy(true);
    setNewError(null);
    try {
      const created = await CategoriesService.create({ name, parentId: newParentId }, userName);
      await loadCategories();
      toast(`Created "${name}"`);
      setNewOpen(false);
      goTo({ kind: 'category', id: created.id });
    } catch (err) {
      setNewError(reason(err, 'That category could not be created'));
    } finally {
      setNewBusy(false);
    }
  };

  // ─── Rename ───
  const [renameTarget, setRenameTarget] = React.useState<ModuleCategory | null>(null);
  const [renameName, setRenameName] = React.useState('');
  const [renameBusy, setRenameBusy] = React.useState(false);
  const [renameError, setRenameError] = React.useState<string | null>(null);
  const openRename = (cat: ModuleCategory) => {
    setRenameTarget(cat);
    setRenameName(cat.name);
    setRenameError(null);
  };
  const doRename = async () => {
    if (!renameTarget) return;
    const name = renameName.trim();
    if (!name) return;
    setRenameBusy(true);
    setRenameError(null);
    try {
      await CategoriesService.rename(renameTarget.id, name, userName);
      await loadCategories();
      toast(`Renamed to "${name}"`);
      setRenameTarget(null);
    } catch (err) {
      setRenameError(reason(err, 'That name could not be saved'));
    } finally {
      setRenameBusy(false);
    }
  };

  // ─── Move to… ───
  const [moveTarget, setMoveTarget] = React.useState<ModuleCategory | null>(null);
  const [moveTo, setMoveTo] = React.useState('__top__');
  const [moveBusy, setMoveBusy] = React.useState(false);
  const [moveError, setMoveError] = React.useState<string | null>(null);
  const openMove = (cat: ModuleCategory) => {
    setMoveTarget(cat);
    setMoveTo(cat.parentId || '__top__');
    setMoveError(null);
  };
  const moveOptions = React.useMemo(() => {
    if (!moveTarget) return [];
    const excluded = new Set(descendantCategoryIds(moveTarget.id, categories));
    return [
      { value: '__top__', label: 'Top level' },
      ...categories
        .filter((c) => !excluded.has(c.id))
        .map((c) => ({ value: c.id, label: categoryPathLabel(c.id, categories) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [moveTarget, categories]);
  const doMove = async () => {
    if (!moveTarget) return;
    const parentId = moveTo === '__top__' ? null : moveTo;
    if (wouldCreateCycle(moveTarget.id, parentId, categories)) {
      setMoveError('That would put the category inside itself.');
      return;
    }
    setMoveBusy(true);
    setMoveError(null);
    try {
      await CategoriesService.move(moveTarget.id, parentId, userName);
      await loadCategories();
      toast(`Moved "${moveTarget.name}"`);
      setMoveTarget(null);
    } catch (err) {
      setMoveError(reason(err, 'That move could not be saved'));
    } finally {
      setMoveBusy(false);
    }
  };

  // ─── Delete (reparents, never deletes a module) ───
  const [deleteTarget, setDeleteTarget] = React.useState<ModuleCategory | null>(null);
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  const deleteDirectModules = deleteTarget
    ? modulesInCategory(modules, deleteTarget.id, categories).length
    : 0;
  const deleteDirectChildren = deleteTarget
    ? findNode(deleteTarget.id, tree)?.children.length || 0
    : 0;
  const deleteParentLabel = deleteTarget
    ? (deleteTarget.parentId && categories.find((c) => c.id === deleteTarget.parentId)?.name) ||
      'the top level'
    : '';
  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const res = await CategoriesService.remove(deleteTarget.id);
      await loadCategories();
      toast(
        `Deleted "${deleteTarget.name}" — ${res.movedModules} module${
          res.movedModules === 1 ? '' : 's'
        } and ${res.reparentedCategories} sub-categor${
          res.reparentedCategories === 1 ? 'y' : 'ies'
        } moved up to "${deleteParentLabel}"`
      );
      if (location.kind === 'category' && location.id === deleteTarget.id) {
        goTo(deleteTarget.parentId ? { kind: 'category', id: deleteTarget.parentId } : ROOT);
      }
      setDeleteTarget(null);
    } catch (err) {
      toast(reason(err, 'That category could not be deleted'), true);
    } finally {
      setDeleteBusy(false);
    }
  };

  return {
    canCreateCategory,
    canEditCategory,
    canDeleteCategory,

    categories,
    categoriesLoaded,
    tree,

    location,
    goTo,
    currentNode,
    showAllCats,
    setShowAllCats,

    counts,
    uncategorizedCount,
    subNodes,
    showUncategorizedEntry,
    entries,

    tableModules,
    crumbs,
    currentLabel,

    tagsInScope: tagsInScopeList,
    selectedTags,
    toggleTag,
    clearTags,
    tagFilterEmptiedResults,

    newOpen,
    newParentId,
    newName,
    setNewName,
    newBusy,
    newError,
    openNew,
    setNewOpen,
    doNew,

    renameTarget,
    renameName,
    setRenameName,
    renameBusy,
    renameError,
    openRename,
    setRenameTarget,
    doRename,

    moveTarget,
    moveTo,
    setMoveTo,
    moveBusy,
    moveError,
    moveOptions,
    openMove,
    setMoveTarget,
    doMove,

    deleteTarget,
    setDeleteTarget,
    deleteBusy,
    deleteDirectModules,
    deleteDirectChildren,
    deleteParentLabel,
    doDelete,
  };
}
